import React, { useState, useMemo, useCallback } from 'react';
import {
  Table,
  Tag,
  Button,
  Card,
  Statistic,
  Row,
  Col,
  notification,
  Space,
  Progress,
  Typography,
  Upload,
  Tabs,
  Tooltip,
  Alert,
} from 'antd';
import {
  UploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  SendOutlined,
  ReloadOutlined,
  FileTextOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from 'util/api';
import './index.less';

const { Text, Title } = Typography;
const { TabPane } = Tabs;

const STRATEGY_LABELS = {
  cin7_cache: 'Cin7 Cache',
  sku_name: 'SKU Name',
  format_parsing: 'Format Parsing',
};

/** Stable key for a preview match (exclusions). */
function getMatchKey(m) {
  if (!m) return '';
  return `${m.skuId}-${m.island}-${m.sapCode}-${m.cin7Name}`;
}

/** Pair NI/SI unmatched lines that belong together (internal only; not shown in UI). */
function groupUnmatchedPairsByRow(items) {
  const map = new Map();
  (items || []).forEach((item) => {
    const ri = item.rowIndex ?? item.row_index ?? 0;
    if (!map.has(ri)) map.set(ri, { ni: null, si: null });
    const g = map.get(ri);
    if (item.island === 'ni') g.ni = item;
    else if (item.island === 'si') g.si = item;
  });
  return [...map.entries()].sort((a, b) => a[0] - b[0]);
}

/** Group Matrix matches by product (beer), then by SKU. */
function groupMatchesByProduct(matches) {
  const byProduct = new Map();
  (matches || []).forEach((m) => {
    const pid = String(m.productMatrixId ?? m.product_matrix_id ?? '');
    const name = m.productName ?? m.product_name ?? 'Unknown product';
    if (!byProduct.has(pid)) {
      byProduct.set(pid, {
        productMatrixId: pid,
        productName: name,
        skus: new Map(),
      });
    }
    const g = byProduct.get(pid);
    const skuId = String(m.skuId);
    if (!g.skus.has(skuId)) {
      g.skus.set(skuId, {
        key: skuId,
        skuId: m.skuId,
        skuName: m.skuName,
        ni: null,
        si: null,
      });
    }
    const row = g.skus.get(skuId);
    if (m.island === 'ni') row.ni = m;
    else if (m.island === 'si') row.si = m;
  });
  return [...byProduct.values()]
    .sort((a, b) => a.productName.localeCompare(b.productName))
    .map((p) => ({
      ...p,
      skuRows: [...p.skus.values()].sort((a, b) => (
        (a.skuName || '').localeCompare(b.skuName || '')
      )),
    }));
}

function renderMatchedIslandCell(match, onRemove) {
  if (!match) return <Text type="secondary">—</Text>;
  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      <div>{match.cin7Name}</div>
      <div>
        <Text type="secondary">SAP </Text>
        {match.sapCode}
      </div>
      <Space size={4} wrap>
        <Tag>{STRATEGY_LABELS[match.strategy] || match.strategy}</Tag>
        {match.willOverwrite ? (
          <Tooltip title={`Will overwrite existing: ${match.existingValue}`}>
            <Tag color="orange" icon={<ExclamationCircleOutlined />}>Overwrite</Tag>
          </Tooltip>
        ) : (
          <Tag color="green" icon={<CheckCircleOutlined />}>New</Tag>
        )}
      </Space>
      <Button
        type="link"
        danger
        size="small"
        icon={<DeleteOutlined />}
        onClick={() => onRemove(match)}
        style={{ paddingLeft: 0 }}
      >
        Remove from commit
      </Button>
    </Space>
  );
}

function renderUnmatchedIslandCell(match) {
  if (!match) return <Text type="secondary">—</Text>;
  return (
    <Space direction="vertical" size={4}>
      <div>{match.cin7Name}</div>
      <div>
        <Text type="secondary">SAP </Text>
        {match.sapCode}
      </div>
    </Space>
  );
}

function renderAlreadyIslandCell(match) {
  if (!match) return <Text type="secondary">—</Text>;
  return (
    <Space direction="vertical" size={4}>
      <div>{match.cin7Name}</div>
      <div>
        <Text type="secondary">SAP </Text>
        {match.sapCode}
        {' — already on '}
        {match.skuName}
        <Text type="secondary">{` #${match.skuId}`}</Text>
      </div>
    </Space>
  );
}

/**
 * Parse a CSV field, handling quoted values that may contain commas.
 */
function parseCsvLine(line) {
  const fields = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current.trim());
  return fields;
}

/**
 * Parse an uploaded CSV file into the row format expected by the backend.
 * Expected columns: NI cin7 name, SAP NI, (empty), SI cin7 name, SAP SI
 */
function parseCsvFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split(/\r?\n/).filter((l) => l.trim());

        const rows = lines.slice(1)
          .map((line) => parseCsvLine(line))
          .filter((r) => (r[0] || r[3])
            && !(r[0] === 'blank' && r[3] === 'blank'))
          .map((r, rowIndex) => ({
            rowIndex,
            northIsland: r[0] || null,
            sapNi: r[1] || null,
            southIsland: r[3] || null,
            sapSi: r[4] || null,
          }));

        resolve(rows);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

const SapImportTable = () => {
  const queryClient = useQueryClient();
  const [spreadsheetRows, setSpreadsheetRows] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [commitProgress, setCommitProgress] = useState({ current: 0, total: 0 });
  const [activeTab, setActiveTab] = useState('matched');
  const [fileName, setFileName] = useState(null);
  /** Match keys the user removed from the commit list (still in preview API result). */
  const [excludedMatchKeys, setExcludedMatchKeys] = useState([]);

  const { data: sapStatus, refetch: refetchStatus } = useQuery({
    queryKey: ['sap-import-status'],
    queryFn: api.getSapImportStatus,
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });

  const handleUpload = useCallback(async (file) => {
    try {
      const rows = await parseCsvFile(file);
      setSpreadsheetRows(rows);
      setFileName(file.name);
      setPreviewData(null);
      notification.success({
        message: 'Spreadsheet Loaded',
        description: `${rows.length} rows parsed from ${file.name}.`,
      });
    } catch (err) {
      notification.error({
        message: 'Parse Error',
        description: `Failed to parse spreadsheet: ${err.message}`,
      });
    }
    return false; // Prevent antd Upload from doing its own upload
  }, []);

  const handlePreview = useCallback(async () => {
    if (!spreadsheetRows) return;
    setPreviewing(true);
    try {
      const results = await api.previewSapImport(spreadsheetRows);
      setPreviewData(results);
      setExcludedMatchKeys([]);
    } catch (err) {
      // Error notification handled by api helper
    } finally {
      setPreviewing(false);
    }
  }, [spreadsheetRows]);

  const handleCommitAll = useCallback(async () => {
    const excluded = new Set(excludedMatchKeys);
    const remaining = (previewData?.matched || []).filter((m) => !excluded.has(getMatchKey(m)));
    if (!remaining.length) return;

    const assignments = remaining.map((m) => ({
      skuId: m.skuId,
      sapCode: m.sapCode,
      island: m.island,
    }));

    setCommitting(true);
    setCommitProgress({ current: 0, total: assignments.length });

    // Commit in batches of 50
    const batchSize = 50;
    let totalUpdated = 0;
    let totalFailed = 0;

    for (let i = 0; i < assignments.length; i += batchSize) {
      const batch = assignments.slice(i, i + batchSize);
      try {
        /* eslint-disable no-await-in-loop */
        const result = await api.commitSapImport(batch);
        /* eslint-enable no-await-in-loop */
        totalUpdated += result.updated?.length || 0;
        totalFailed += result.failed?.length || 0;
      } catch (err) {
        totalFailed += batch.length;
      }
      const done = Math.min(i + batchSize, assignments.length);
      setCommitProgress({ current: done, total: assignments.length });
    }

    if (totalFailed > 0) {
      notification.warning({
        message: 'SAP Import Finished With Errors',
        description: `${totalUpdated} SAP codes assigned; ${totalFailed} could not be saved.`,
      });
    } else {
      notification.success({
        message: 'SAP Import Complete',
        description: `${totalUpdated} SAP codes assigned.`,
      });
    }

    setCommitting(false);
    setCommitProgress({ current: 0, total: 0 });
    queryClient.invalidateQueries({ queryKey: ['sap-import-status'] });
    refetchStatus();
  }, [previewData, excludedMatchKeys, queryClient, refetchStatus]);

  const removeMatchFromCommit = useCallback((m) => {
    const k = getMatchKey(m);
    setExcludedMatchKeys((prev) => (prev.includes(k) ? prev : [...prev, k]));
  }, []);

  const remainingMatched = useMemo(() => {
    if (!previewData?.matched?.length) return [];
    const excluded = new Set(excludedMatchKeys);
    return previewData.matched.filter((m) => !excluded.has(getMatchKey(m)));
  }, [previewData, excludedMatchKeys]);

  const productsMatched = useMemo(
    () => groupMatchesByProduct(remainingMatched),
    [remainingMatched],
  );

  const unmatchedPairs = useMemo(
    () => groupUnmatchedPairsByRow(previewData?.unmatched),
    [previewData?.unmatched],
  );

  const productsAlreadyAssigned = useMemo(
    () => groupMatchesByProduct(previewData?.alreadyAssigned || []),
    [previewData?.alreadyAssigned],
  );

  const hasPreview = previewData !== null;
  const matchedCount = previewData?.stats?.matched || 0;
  const unmatchedCount = previewData?.stats?.unmatched || 0;
  const alreadyCount = previewData?.stats?.alreadyAssigned || 0;

  const unmatchedTableData = useMemo(
    () => unmatchedPairs.map(([pairKey, { ni, si }]) => ({
      key: pairKey,
      ni,
      si,
    })),
    [unmatchedPairs],
  );

  const skuMatchedColumns = useMemo(() => [
    {
      title: 'SKU',
      key: 'sku',
      width: 220,
      render: (_, r) => (
        <div>
          <div>{r.skuName}</div>
          <Text type="secondary">{`#${r.skuId}`}</Text>
        </div>
      ),
    },
    {
      title: 'Matched NI',
      key: 'ni',
      width: 300,
      render: (_, r) => renderMatchedIslandCell(r.ni, removeMatchFromCommit),
    },
    {
      title: 'Matched SI',
      key: 'si',
      width: 300,
      render: (_, r) => renderMatchedIslandCell(r.si, removeMatchFromCommit),
    },
  ], [removeMatchFromCommit]);

  const unmatchedColumns = useMemo(() => [
    {
      title: 'Unmatched NI',
      key: 'ni',
      width: 360,
      render: (_, r) => renderUnmatchedIslandCell(r.ni),
    },
    {
      title: 'Unmatched SI',
      key: 'si',
      width: 360,
      render: (_, r) => renderUnmatchedIslandCell(r.si),
    },
  ], []);

  const skuAlreadyColumns = useMemo(() => [
    {
      title: 'SKU',
      key: 'sku',
      width: 220,
      render: (_, r) => (
        <div>
          <div>{r.skuName}</div>
          <Text type="secondary">{`#${r.skuId}`}</Text>
        </div>
      ),
    },
    {
      title: 'NI',
      key: 'ni',
      width: 300,
      render: (_, r) => renderAlreadyIslandCell(r.ni),
    },
    {
      title: 'SI',
      key: 'si',
      width: 300,
      render: (_, r) => renderAlreadyIslandCell(r.si),
    },
  ], []);

  return (
    <div className="sap-import-table">
      {/* Current Status */}
      {sapStatus && (
        <div className="sap-import-summary">
          <Row gutter={16}>
            <Col span={6}>
              <Card size="small">
                <Statistic
                  title="Total SKUs"
                  value={sapStatus.totalSkus}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic
                  title="With NI SAP"
                  value={sapStatus.withNi}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic
                  title="With SI SAP"
                  value={sapStatus.withSi}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic
                  title="Missing SAP"
                  value={sapStatus.withoutEither}
                  valueStyle={{ color: sapStatus.withoutEither > 0 ? '#faad14' : '#52c41a' }}
                />
              </Card>
            </Col>
          </Row>
        </div>
      )}

      {/* Upload Section */}
      {!hasPreview && (
        <div className="upload-section">
          <Upload
            accept=".csv"
            showUploadList={false}
            beforeUpload={handleUpload}
          >
            <Button icon={<UploadOutlined />} size="large" type="primary">
              Upload SAP Generator CSV
            </Button>
          </Upload>
          {fileName && (
            <div style={{ marginTop: 12 }}>
              <Tag icon={<FileTextOutlined />} color="green">{fileName}</Tag>
              <Text type="secondary">
                {` — ${spreadsheetRows?.length || 0} rows loaded`}
              </Text>
            </div>
          )}
          {spreadsheetRows && (
            <div style={{ marginTop: 16 }}>
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handlePreview}
                loading={previewing}
              >
                Match SAP Codes to SKUs
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Preview Results */}
      {hasPreview && (
        <>
          {/* Summary Row */}
          <div className="sap-import-summary">
            <Row gutter={16}>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="Total Entries"
                    value={previewData.stats.total}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Tooltip
                  title={
                    excludedMatchKeys.length > 0
                      ? `${matchedCount} matched in preview; ${excludedMatchKeys.length} excluded from commit`
                      : undefined
                  }
                >
                  <Card
                    size="small"
                    className={`summary-card${activeTab === 'matched' ? ' active-matched' : ''}`}
                    onClick={() => setActiveTab('matched')}
                  >
                    <Statistic
                      title="Matched"
                      value={remainingMatched.length}
                      valueStyle={{ color: '#52c41a' }}
                      prefix={<CheckCircleOutlined />}
                      suffix={
                        excludedMatchKeys.length > 0 && matchedCount !== remainingMatched.length
                          ? `/ ${matchedCount}`
                          : null
                      }
                    />
                  </Card>
                </Tooltip>
              </Col>
              <Col span={6}>
                <Card
                  size="small"
                  className={`summary-card${activeTab === 'unmatched' ? ' active-unmatched' : ''}`}
                  onClick={() => setActiveTab('unmatched')}
                >
                  <Statistic
                    title="Unmatched"
                    value={unmatchedCount}
                    valueStyle={{ color: unmatchedCount > 0 ? '#ff4d4f' : '#52c41a' }}
                    prefix={<CloseCircleOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card
                  size="small"
                  className={`summary-card${activeTab === 'already' ? ' active-assigned' : ''}`}
                  onClick={() => setActiveTab('already')}
                >
                  <Statistic
                    title="Already Assigned"
                    value={alreadyCount}
                    valueStyle={{ color: '#1677ff' }}
                    prefix={<CheckCircleOutlined />}
                  />
                </Card>
              </Col>
            </Row>
          </div>

          {/* Strategy breakdown */}
          {previewData.stats.byStrategy && (
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Text type="secondary">Matched by:</Text>
                {Object.entries(previewData.stats.byStrategy)
                  .filter(([, count]) => count > 0)
                  .map(([strategy, count]) => (
                    <Tag key={strategy}>
                      {STRATEGY_LABELS[strategy] || strategy}
                      {': '}
                      {count}
                    </Tag>
                  ))}
              </Space>
            </div>
          )}

          {/* Commit progress */}
          {committing && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Text>
                {'Assigning SAP codes... '}
                {commitProgress.current}
                {' / '}
                {commitProgress.total}
              </Text>
              <Progress
                percent={Math.round((commitProgress.current / commitProgress.total) * 100)}
                status="active"
              />
            </Card>
          )}

          {/* Actions */}
          <div className="sap-import-actions">
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  setPreviewData(null);
                  setSpreadsheetRows(null);
                  setFileName(null);
                  setExcludedMatchKeys([]);
                }}
              >
                Start Over
              </Button>
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleCommitAll}
                disabled={remainingMatched.length === 0 || committing}
                loading={committing}
              >
                {`Commit all (${remainingMatched.length})`}
              </Button>
            </Space>
          </div>

          {excludedMatchKeys.length > 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={`${excludedMatchKeys.length} match(es) removed from the commit list.`}
              description="Commit will only apply the matches shown below. Run preview again to reset exclusions."
            />
          )}

          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab={`Matched (${remainingMatched.length})`} key="matched">
              {productsMatched.length === 0 ? (
                <Text type="secondary">
                  {previewData.matched?.length
                    ? 'All matches were removed. Remove fewer exclusions, or Start Over and preview again.'
                    : 'No matches from this preview.'}
                </Text>
              ) : (
                productsMatched.map((product) => (
                  <div
                    key={product.productMatrixId}
                    className="sap-import-product-block"
                  >
                    <Title level={5}>{product.productName}</Title>
                    <Table
                      columns={skuMatchedColumns}
                      dataSource={product.skuRows}
                      rowKey="key"
                      size="small"
                      pagination={false}
                      scroll={{ x: 900 }}
                    />
                  </div>
                ))
              )}
            </TabPane>
            <TabPane tab={`Unmatched (${unmatchedCount})`} key="unmatched">
              {unmatchedTableData.length === 0 ? (
                <Text type="secondary">Nothing unmatched.</Text>
              ) : (
                <Table
                  columns={unmatchedColumns}
                  dataSource={unmatchedTableData}
                  rowKey="key"
                  size="small"
                  pagination={{
                    defaultPageSize: 50,
                    showSizeChanger: true,
                    pageSizeOptions: ['25', '50', '100'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                  }}
                  scroll={{ x: 900 }}
                />
              )}
            </TabPane>
            <TabPane tab={`Already Assigned (${alreadyCount})`} key="already">
              {productsAlreadyAssigned.length === 0 ? (
                <Text type="secondary">No products already had these SAP codes.</Text>
              ) : (
                productsAlreadyAssigned.map((product) => (
                  <div
                    key={product.productMatrixId}
                    className="sap-import-product-block"
                  >
                    <Title level={5}>{product.productName}</Title>
                    <Table
                      columns={skuAlreadyColumns}
                      dataSource={product.skuRows}
                      rowKey="key"
                      size="small"
                      pagination={false}
                      scroll={{ x: 900 }}
                    />
                  </div>
                ))
              )}
            </TabPane>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default SapImportTable;
